import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, INR2, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark } from "lucide-react";

const PURPOSES = ["Business", "Wedding", "Emergency", "Education", "Home Renovation"];

export default function PledgeModal({ open, onOpenChange, onDone, balance = 0 }) {
  const [grams, setGrams] = useState(1);
  const [tenure, setTenure] = useState(12);
  const [purpose, setPurpose] = useState("Business");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pledge", { grams: Number(grams), tenure_months: Number(tenure), purpose });
      setResult(data.pledge);
      toast.success("Pledge pre-approved (MOCKED)");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const insufficient = Number(grams) > Number(balance);

  const close = () => {
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setResult(null); onOpenChange(v); }}>
      <DialogContent data-testid="pledge-modal" className="max-w-md rounded-3xl bg-brand-ivory border-brand-line">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-brand-charcoal flex items-center gap-2">
            <Landmark className="text-brand-primary" size={20} /> Gold Pledge+
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <>
            <p className="text-sm text-brand-slate">Get an instant loan at 9.5% p.a. using your gold as collateral. 75% loan-to-value.</p>
            <div>
              <Label className="text-brand-charcoal text-sm font-semibold">Grams to pledge</Label>
              <Input
                data-testid="pledge-grams"
                type="number"
                step="0.01"
                value={grams}
                onChange={(e) => setGrams(e.target.value)}
                className="mt-2 h-12 rounded-xl border-brand-line bg-brand-sand tabular-nums"
              />
              <div className="mt-1 text-xs text-brand-slate tabular-nums">Available: {Number(balance).toFixed(4)}g</div>
              {insufficient && <div className="text-xs text-brand-primary mt-1">Insufficient balance</div>}
            </div>
            <div>
              <Label className="text-brand-charcoal text-sm font-semibold">Tenure (months)</Label>
              <Input
                data-testid="pledge-tenure"
                type="number"
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                className="mt-2 h-12 rounded-xl border-brand-line bg-brand-sand tabular-nums"
              />
            </div>
            <div>
              <Label className="text-brand-charcoal text-sm font-semibold">Purpose</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PURPOSES.map((p) => (
                  <button
                    key={p}
                    data-testid={`pledge-purpose-${p}`}
                    onClick={() => setPurpose(p)}
                    className={`h-10 px-4 rounded-full text-sm font-semibold border ${purpose === p ? "bg-brand-primary text-brand-ivory border-brand-primary" : "bg-brand-ivory text-brand-charcoal border-brand-line"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <Button
              data-testid="pledge-submit"
              onClick={submit}
              disabled={loading || insufficient || !grams}
              className="w-full h-14 rounded-full bg-brand-primary hover:bg-brand-primary-soft text-brand-ivory font-semibold shadow-card"
            >
              {loading ? "Checking…" : "Check eligibility"}
            </Button>
          </>
        ) : (
          <div data-testid="pledge-result" className="rounded-2xl bg-brand-sand2 border border-brand-line text-brand-cream p-5 space-y-2">
            <div className="text-xs uppercase tracking-wider text-brand-accent">Pre-approved</div>
            <div className="font-display text-3xl tabular-nums text-brand-cream">{INR2(result.loan_amount_inr)}</div>
            <div className="text-sm text-brand-slate">at {result.interest_rate_pct}% p.a. · {result.tenure_months} months</div>
            <Button data-testid="pledge-close" onClick={close} className="mt-3 w-full h-12 rounded-full bg-gold-gradient text-brand-page font-semibold">
              Great, close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
